<script>
import ModelFilter from "@/router/views/purchase_payments/filter";
import ModelIndex from "../models/index-v3";
import StatusWidget from "@/components/widgets/statuses/status";
import Swal from "sweetalert2";

export default {
  extends: ModelIndex,
  components: {
    ModelFilter
  },
  page: {
    title: "Odoo Account Payments"
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("menuitems.odoo_account_payments.text"));
      }
    },
    resourceRoute: {default: "odoo_account_payments"},
    modelVariable: {default: "odoo_account_payment"},
    denyCreate: {
      type: Boolean,
      default: true
    },
    denyOdooSync: {
      type: Boolean,
      default() {
        return !this.$auth.hasPermissionTo('odoo_purchase_invoices.sync')
      }
    }
  },
  data() {
    return {
      title: this.lodash.startCase(this.$t("odoo_account_payments.plural")),
      items: [
        {
          text: this.lodash.startCase(this.$t("odoo_account_payments.singular")),
          href: "/odoo_account_payments",
          active: true
        }
      ],
      sortBy:
          (this.$route.query.sort &&
              this.$route.query.sort
                  .split("|")
                  .shift()
                  .split(",")
                  .shift()) ||
          "odoo_id",
      sortDesc:
          (this.$route.query.sort &&
              this.$route.query.sort
                  .split("|")
                  .shift()
                  .split(",")
                  .pop() === "desc") ||
          true,
      filterColumns: {},
      model: this.modelVariable,
      deleting: false,
      disableSyncButton: false,
    }
  },
  computed: {
    columns() {
      return {
        ["odoo_account_payment"]: [
          {
            key: "name",
            label: this.lodash.startCase(this.$t("odoo_account_payments.invoice_number")),
            thStyle: {width: "175px"},
            sortable: true,
          },
          {
            key: "ticket",
            label: this.lodash.startCase(this.$t("purchase_plans.sales_plan_id")),
            thStyle: {width: "125px"},
            component: {
              props: ["item"],
              methods: {
                label(item) {
                  if (item.ticket_id) {
                    return `${item.ticket_id}:${this.lodash.startCase(this.$t(`tickets.customer_types.${item.ticket.customer_type}`))}`
                  }
                  if(item.sales_plan?.id) {
                    return `${item.sales_plan.name ? item.sales_plan.name : item.sales_plan.id}`
                  }
                  return '-'
                },
                link(item) {
                  if (item.ticket_id) {
                    return `/tickets/${item.ticket_id}`
                  }
                  if(item.sales_plan?.id) {
                    return `/sales_plans/${item.sales_plan.id}`
                  }
                  return '#'
                }
              },
              template:
                  `
                    <div>
                      <router-link :to="link(item)">{{label(item)}}</router-link>
                    </div>
                  `
            },
          },
          {
            key: "purchase_orders",
            label: this.lodash.startCase(this.$t("odoo_account_payments.purchase_order_id")),
            thStyle: {width: "200px"},
            component: {
              props: ["item"],
              methods: {
                label(purchaseOrder) {
                  if (!purchaseOrder) {
                    return '-'
                  }
                  return `${purchaseOrder.id} (${purchaseOrder.customer?.name})`
                },
              },
              template:
                  '<div v-for="(purchaseOrder, index) in item.purchase_orders" :key="`purchase-order-${purchaseOrder.id}`"><router-link :to="`/purchase_orders/${purchaseOrder.id}`">{{ label(purchaseOrder) }}</router-link><span v-if="index < item.purchase_orders.length - 1">, </span></div>'
            }
          },
          {
            key: "date",
            label: this.lodash.startCase(this.$t(`${this.resourceRoute}.date`)),
            thStyle: {width: "150px"},
            sortable: true,
            formatter: value => this.$moment(value).format("DD MMMM YYYY"),
          },
          {
            key: "destination_account_id",
            label: this.lodash.startCase(this.$t("odoo_account_payments.destination")),
            component: {
              props: ["item"],
              computed: {
                destinationBankAccount() {
                  return this.item.destination_account
                },
                destinationCustomer() {
                  return this.destinationBankAccount?.customer
                },
                label() {
                  if (!this.destinationBankAccount) {
                    return '-'
                  }
                  if (this.destinationBankAccount?.name) {
                    return `${this.destinationBankAccount?.name} - ${this.destinationBankAccount?.account_number} - ${this.destinationBankAccount?.bank?.name}`
                  }
                  return `${this.destinationCustomer?.name} - ${this.destinationBankAccount?.account_number} - ${this.destinationBankAccount?.bank?.name}`
                }
              },
              template: '<router-link :to="`/customers/${(destinationCustomer || {}).id}`">{{ label }}</router-link>'
            }
          },
          {
            key: "amount_total",
            label: this.lodash.startCase(this.$t("odoo_account_payments.amount")),
            thClass: "text-end",
            tdClass: "text-end",
            thStyle: {width: "150px"},
            formatter: value => this.$numeral(value).format("$ 0,0.[00]"),
          },
          {
            key: 'state',
            label: this.lodash.startCase(this.$t("odoo_account_payments.state")),
            component: {
              props: ["item"],
              template:`
                <status-badge
                    v-if="item.state"
                    entity="odoo_account_move"
                    type="status"
                    :status="item.state"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
          {
            key: 'payment_state',
            label: this.lodash.startCase(this.$t("odoo_account_payments.payment_state")),
            component: {
              props: ["item"],
              template:`
                <status-badge
                    v-if="item.payment_state"
                    entity="odoo_account_move"
                    type="payment_status"
                    :status="item.payment_state"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
        ]
      };
    }
  },
  methods: {
    getModelName(value) {
      return value["id"];
    },
    delete(value) {
      return this.$axios.delete(`/payments/${this.getModelKey(value)}`);
    },
    fetchModels(page = 1) {
      return this.$axios
          .get("/v1/odoo_account_moves", {
            params: {
              'filter[move_type]': 'in_invoice',
              page: page,
              per_page: this.perPage,
              search: this.search,
              include: [
                "odoo_account_move_lines.odoo_account_move_lineable.odoo_purchase_order.purchase_order.ticket",
                "odoo_account_move_lines.odoo_account_move_lineable.odoo_purchase_order.purchase_order.customer",
                "ticket",
                "destination_account.customer",
                "destination_account.bank",
                "sales_plan",
              ].join(","),
              sort: this.sortDesc ? `-${this.sortBy}` : this.sortBy,
              ...this.$route.params,
              ...this.filterParams
            }
          })
          .then(response => {
            const {
              data: {data, meta}
            } = response;
            this.models = data.map(datum => {
              let purchaseOrders = datum.odoo_account_move_lines
                  .filter(({display_type}) => display_type === 'product')
                  .map(({odoo_account_move_lineable}) => odoo_account_move_lineable?.odoo_purchase_order?.purchase_order)
              let uniquePurchaseOrders = this.lodash.uniqBy(purchaseOrders, 'id').filter(order => order)
              let tickets = uniquePurchaseOrders.map(({ticket}) => ticket)
              let uniqueTickets = this.lodash.uniqBy(tickets, 'id')

              return {
                ...datum,
                ...this.modelPolicy(datum),
                purchase_orders: uniquePurchaseOrders,
                tickets: uniqueTickets,
              };
            });
            this.page = Number(meta.current_page);
            this.perPage = Number(meta.per_page);
            this.from = Number(meta.from);
            this.total = Number(meta.total);

            this.stats = [
              {
                title: this.lodash.startCase(this.$t("odoo_account_payments.count")),
                value: this.$numeral(this.total).format("0,0 a"),
                valueTitle: this.$numeral(this.total).format("0,0"),
                wrapperClass: "col-12 col-md-6 col-xl-4",
                component: StatusWidget
              }
            ];

            return response;
          })
          .catch(error => this.failedAlert(error));
    },
    modelPolicy() {
      return {
        _denyShow: false,
        _denyEdit: true,
        _denyDelete: false,
      }
    },
    onClickSyncToday() {
      this.disableSyncButton = true
      return this.$axios.post('/v1/odoo_account_moves/updated_today/sync', {
        'move_type': 'in_invoice'
      })
          .then(() => {
            return this.syncQueueInProgressAlert();
          })
          .catch(e => this.failedAlert(e))
          .finally(() => {
            this.disableSyncButton = false
          })
    },
    syncQueueInProgressAlert() {
      return Swal.fire({
        title: `Sync Purchase Invoice in Progress`,
        type: "primary",
        confirmButtonClass: "btn btn-success",
        confirmButtonText: this.$t("ok"),
        buttonsStyling: false
      }).then((res) => {
        if (res.isConfirmed) {
          this.$router.go(0)
        }
      });
    },
    failedAlert(error) {
      if (error?.response?.status === 422) {
        return Swal.fire({
          title: this.$t("Warning") + "!",
          text: this.$t("odoo_account_payments.there_is_sync_purchase_invoice_queue_job_in_progress"),
          type: "warning",
          confirmButtonClass: "btn btn-warning btn-fill",
          buttonsStyling: false
        });
      }
      return Swal.fire({
        title: this.$t("Failed") + "!",
        text: (error.response && (error.response.data || {}).message) || error.message,
        type: "danger",
        confirmButtonClass: "btn btn-danger btn-fill",
        buttonsStyling: false
      });
    },
  }
};
</script>

<template>
  <Layout>
    <!-- <PageHeader v-if="title" :title="title" :items="items" /> -->
    <div v-if="stats.length" class="row">
      <div v-for="stat of stats" :key="stat.icon" class="col-6 col-md-4 col-xl-3">
        <Stat :loading="busy" v-bind="stat"/>
      </div>
    </div>
    <div v-if="!lodash.isEmpty(filterColumns)" class="row">
      <model-filter
          v-model="filter"
          :columns="filterColumns"
          :filterHandler="filterHandler"
          :modelVariable="modelVariable"
      />
    </div>
    <div class="row">
      <model-index
          ref="table"
          :busy.sync="busy"
          :columns="columns"
          :filter-params.sync="filterParams"
          :from="from"
          :loading="loading"
          :models="models"
          :models-paginated="modelsPaginated"
          :page.sync="page"
          :per-page.sync="perPage"
          :search.sync="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :total="totalFiltered"
          class="col-12"
          v-bind="$props"
          @create-click="handleCreate"
          @export-click="handleExport"
          @show-click="handleShow"
          @edit-click="handleEdit"
          @delete-click="handleDelete"
      >
        <template v-slot:card-tools>
          <b-button
              :disabled="disableSyncButton"
              class="create-button me-2"
              variant="primary"
              @click="onClickSyncToday"
              v-if="!denyOdooSync"
          >
            {{ $t(`${resourceRoute}.sync_updated_today_button_text`) }}
          </b-button>
        </template>
      </model-index>
    </div>
  </Layout>
</template>
